import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { LoaderIcon } from 'lucide-react';
import { DashboardCard } from '@/components/ui/dashboard-card.tsx';
import { useSession } from '@/services/providers/use-session.ts';
import { usePatchUserProfileMutation } from '@/features/user-profile/hooks/mutations/use-patch-user-profile.ts';
import { UserSchema } from '@/features/users/schemas/users.schema.ts';

const personalInformationSchema = UserSchema.pick({
  firstName: true,
  lastName: true,
});

type PersonalInformationForm = {
  firstName: string;
  lastName: string;
};

export function PersonalInformationCard() {
  const user = useSession();
  const { isLoading } = user;

  const form = useForm<PersonalInformationForm>({
    resolver: zodResolver(personalInformationSchema),
    defaultValues: {
      firstName: user.firstName,
      lastName: user.lastName,
    },
  });

  const { mutate: updateUserProfile, isPending } = usePatchUserProfileMutation({
    onSuccess: () => {
      form.reset({
        firstName: form.getValues('firstName'),
        lastName: form.getValues('lastName'),
      });
    },
  });

  const hasChanges =
    form.getValues('lastName') !== user.lastName ||
    form.getValues('firstName') !== user.firstName;

  const handleSubmit = (data: PersonalInformationForm) => {
    if (!hasChanges) return;

    updateUserProfile({
      userId: user.userId,
      firstName: data.firstName,
      lastName: data.lastName,
    });
  };

  const handleCancel = () => {
    form.reset({
      firstName: user.firstName,
      lastName: user.lastName,
    });
  };

  useEffect(() => {
    form.reset({
      firstName: user.firstName,
      lastName: user.lastName,
    });
  }, [isLoading, user.firstName, user.lastName]);

  return (
    <DashboardCard className="min-h-[260px] border p-4" isLoading={isLoading}>
      <CardHeader className="flex-row justify-between p-0">
        <div>
          <CardTitle>Personal Information</CardTitle>
          <CardDescription>Update your personal details.</CardDescription>
        </div>
        <div className="flex items-center gap-4">
          {hasChanges && (
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          )}
          <Button
            type="button"
            disabled={!hasChanges || isPending || !form.formState.isValid}
            className="min-w-[120px]"
            onClick={() => handleSubmit(form.getValues())}
          >
            {isPending ? (
              <>
                <LoaderIcon className="mr-1.5 size-4 animate-spin" aria-hidden="true" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <Form {...form}>
          <form className="space-y-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter your first name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter your last name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input id="email" value={user.email} disabled className="bg-muted" />
              <p className="text-muted-foreground text-sm">Email address cannot be changed.</p>
            </div>
          </form>
        </Form>
      </CardContent>
    </DashboardCard>
  );
}
